import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import { Button, StatusBadge, BackButton } from '../components/common';
import { navigateBack } from '../utils/navigationUtils';
import { Heading, Text, Typography } from '../components/common/Typography';
import { Card } from '../components/common/Card';
import Tabs from '../components/common/Tabs';
import { homeSectionHeading } from '../components/home/homeDashboardStyles';
import { getPlayerNameColor, getPlayerFullName } from '../utils/playerUtils';
import ClubAnalysisKpiGrid, { ANALYTICS_ICON_BG } from '../components/clubs/analysis/ClubAnalysisKpiGrid';
import ClubAnalysisRecentSessions from '../components/clubs/analysis/ClubAnalysisRecentSessions';
import ClubAnalysisLeaderboardFilters from '../components/clubs/analysis/ClubAnalysisLeaderboardFilters';
import AdminLeaderboardList from '../components/clubs/analysis/AdminLeaderboardList';
import ClubAnalysisPlayerFilters from '../components/clubs/analysis/ClubAnalysisPlayerFilters';
import ClubAnalysisSessionSummaryList from '../components/clubs/analysis/ClubAnalysisSessionSummaryList';
import { SeasonProvider, useSeason } from '../context/SeasonContext';
import EloHistoryChart from '../components/player/EloHistoryChart';
import GameHistoryTable from '../components/player/GameHistoryTable';
import {
  fetchPlayerClubStats,
  fetchPlayerUnifiedGameHistory,
  fetchPlayerEloHistory,
} from '../services/playerService';
import PlayerSearchInput from '../components/common/PlayerSearchInput';
import Pagination from '../components/common/Pagination';
import {
  isSelectableClubRosterPlayer,
  normalizeClubPlayerForSearch,
  sortClubPlayersByName,
} from '../utils/clubPlayerSearchUtils';

// Progress Bar Component using design system colors
const ProgressBar = ({ percentage, label, variant = 'primary' }) => {
  const getVariantClasses = (variant) => {
    switch (variant) {
      case 'primary': return 'bg-blue-500';
      case 'success': return 'bg-green-500';
      case 'warning': return 'bg-yellow-500';
      case 'danger': return 'bg-red-500';
      case 'info': return 'bg-blue-500';
      case 'male': return 'bg-male';
      case 'female': return 'bg-female';
      case 'mixed': return 'bg-purple-500';
      case 'levels': return 'bg-indigo-500';
      default: return 'bg-accent';
    }
  };

  return (
    <div className="w-full bg-surface-subtle rounded-full h-2">
      <div 
        className={`${getVariantClasses(variant)} h-2 rounded-full transition-all duration-300`}
        style={{ width: `${Math.min(percentage, 100)}%` }}
      ></div>
    </div>
  );
};

// General Dashboard Content Component
const GeneralDashboardContent = ({ analytics, season, club, userRole }) => {
  const navigate = useNavigate();

  const kpiItems = [
    {
      key: 'players',
      label: 'Total players',
      value: analytics.totalPlayers,
      subtitle: `${analytics.activePlayers} active`,
    },
    {
      key: 'sessions',
      label: 'Total sessions',
      value: analytics.totalSessions,
      subtitle: `${analytics.sessionsThisMonth} this month`,
    },
    {
      key: 'avg-size',
      label: 'Avg session size',
      value: analytics.averageSessionSize,
      subtitle: 'players per session',
    },
    {
      key: 'active-30',
      label: 'Active (30d)',
      value: `${analytics.activePlayers30DaysPercentage}%`,
      subtitle: `${analytics.activePlayers30Days} of ${analytics.totalPlayers}`,
    },
  ];

  const handleViewSession = (session) => {
    if (!session?.id || !club?.id) return;
    navigate(`/club/${club.id}/session-wrapup/${session.id}`, {
      state: {
        fromClubDashboard: true,
        selectedClub: club,
        userRole,
      },
    });
  };

  return (
  <div className="space-y-6 sm:space-y-8">
    <ClubAnalysisKpiGrid items={kpiItems} />

    {/* Detailed Analytics */}
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2 lg:gap-8">
      <Card variant="default" size="large">
        <h3 className={`${homeSectionHeading} mb-4`}>Gender demographics</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Typography variant="body" className={getPlayerNameColor('male')}>{analytics.genderSplit.male} Male</Typography>
            <Typography variant="body" className={`font-semibold ${getPlayerNameColor('male')}`}>{analytics.genderSplit.male}</Typography>
          </div>
          <ProgressBar 
            percentage={analytics.totalPlayers > 0 ? Math.round((analytics.genderSplit.male / analytics.totalPlayers) * 100) : 0} 
            label="Male" 
            variant="male"
          />
          
          <div className="flex justify-between items-center">
            <Typography variant="body" className={getPlayerNameColor('female')}>{analytics.genderSplit.female} Female</Typography>
            <Typography variant="body" className={`font-semibold ${getPlayerNameColor('female')}`}>{analytics.genderSplit.female}</Typography>
          </div>
          <ProgressBar 
            percentage={analytics.totalPlayers > 0 ? Math.round((analytics.genderSplit.female / analytics.totalPlayers) * 100) : 0} 
            label="Female" 
            variant="female"
          />
          
        </div>
      </Card>

      <Card variant="default" size="large">
        <h3 className={`${homeSectionHeading} mb-4`}>Average ELO ratings</h3>
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <Typography variant="body">Mixed Doubles</Typography>
            <Typography variant="h3" className="font-semibold">{Math.round(analytics.averageElo.mixed)}</Typography>
          </div>
          <ProgressBar 
            percentage={Math.min((analytics.averageElo.mixed / 2000) * 100, 100)} 
            label="Mixed" 
            variant="mixed"
          />
          
          <div className="flex justify-between items-center">
            <Typography variant="body">Levels</Typography>
            <Typography variant="h3" className="font-semibold">{Math.round(analytics.averageElo.levels)}</Typography>
          </div>
          <ProgressBar 
            percentage={Math.min((analytics.averageElo.levels / 2000) * 100, 100)} 
            label="Levels" 
            variant="levels"
          />
        </div>
      </Card>
    </div>

    <Card variant="default" size="large">
      <h3 className={`${homeSectionHeading} mb-4`}>Recent sessions</h3>
      <ClubAnalysisRecentSessions
        sessions={analytics.recentSessions}
        club={club}
        userRole={userRole}
        onViewSession={handleViewSession}
      />
    </Card>
  </div>
  );
};

// Club Players Dashboard Content Component
const ClubPlayersDashboardContent = ({ club, season, onViewAnalysis }) => {
  const [allPlayers, setAllPlayers] = useState([]); // Full dataset: all players, both ELO types
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eloFilter, setEloFilter] = useState('mixed'); // 'mixed' or 'levels'
  const [genderFilter, setGenderFilter] = useState('all'); // 'all', 'male', 'female'
  const [seasons, setSeasons] = useState([]);
  const [leaderboardSeason, setLeaderboardSeason] = useState(null); // Season for leaderboard; defaults to context season
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(30);

  // Sync leaderboardSeason when context season loads or changes
  useEffect(() => {
    setLeaderboardSeason(season);
  }, [season?.id]);

  // Fetch seasons for the club (for season filter dropdown)
  useEffect(() => {
    if (!club?.id) return;
    const fetchSeasons = async () => {
      const { data, error } = await supabase
        .from('seasons')
        .select('id, name, season_number, season_status')
        .eq('club_id', club.id)
        .order('season_number', { ascending: false });
      if (!error) setSeasons(data || []);
    };
    fetchSeasons();
  }, [club?.id]);

  // Fetch only when club or leaderboardSeason changes; ELO/gender filtering is client-side
  useEffect(() => {
    if (!club?.id) return;
    fetchPlayersData();
  }, [club?.id, leaderboardSeason?.id]);

  const fetchPlayersData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Fetch players from the club
      const { data: playersData, error: playersError } = await supabase
        .from('players_club')
        .select(`
          player_id,
          players!inner(
            id,
            first_name,
            last_name,
            email,
            gender,
            avatar_url,
            is_guest,
            is_active,
            merged_at,
            merged_into_user_id,
            deletion_requested_at
          )
        `)
        .eq('club_id', club.id)
        .eq('is_active', true);

      if (playersError) throw playersError;

      const visiblePlayersData = (playersData || []).filter((row) =>
        isSelectableClubRosterPlayer(row.players)
      );
      const playerIds = visiblePlayersData.map(p => p.player_id);
      if (playerIds.length === 0) {
        setAllPlayers([]);
        return;
      }

      // 2. Fetch ELO and session counts in parallel
      const eloQuery = supabase
        .from('player_club_elo_ratings')
        .select('player_id, mixed_elo_rating, levels_elo_rating')
        .eq('club_id', club.id)
        .in('player_id', playerIds);

      let gamesQuery = supabase
        .from('player_game_history')
        .select('player_id, session_id, club_id, season_id')
        .eq('club_id', club.id)
        .in('player_id', playerIds);

      // Filter by season in DB when selected (reduces rows returned)
      if (leaderboardSeason?.id) {
        gamesQuery = gamesQuery.eq('season_id', leaderboardSeason.id);
      }

      const [eloResult, gamesResult] = await Promise.all([eloQuery, gamesQuery]);
      const { data: eloData, error: eloError } = eloResult;
      const { data: playerGames, error: gamesError } = gamesResult;

      if (eloError) throw eloError;
      if (gamesError) throw gamesError;

      const eloMap = {};
      (eloData || []).forEach(elo => {
        eloMap[elo.player_id] = {
          mixed_elo_rating: elo.mixed_elo_rating || 1200,
          levels_elo_rating: elo.levels_elo_rating || 1200
        };
      });

      const sessionCountMap = {};
      if (playerGames && playerGames.length > 0) {
        const sessionsByPlayer = {};
        playerGames.forEach((row) => {
          if (!row.session_id) return;
          if (!sessionsByPlayer[row.player_id]) {
            sessionsByPlayer[row.player_id] = new Set();
          }
          sessionsByPlayer[row.player_id].add(row.session_id);
        });
        Object.entries(sessionsByPlayer).forEach(([playerId, sessionSet]) => {
          sessionCountMap[playerId] = sessionSet.size;
        });
      }

      // Combine data (no gender filter; we filter client-side)
      const combinedData = visiblePlayersData.map(player => {
        const eloRatings = eloMap[player.player_id] || { mixed_elo_rating: 1200, levels_elo_rating: 1200 };
        return {
          ...player,
          session_count: sessionCountMap[player.player_id] || 0,
          mixed_elo_rating: eloRatings.mixed_elo_rating,
          levels_elo_rating: eloRatings.levels_elo_rating
        };
      });

      setAllPlayers(combinedData);
    } catch (err) {
      console.error('Error fetching players data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Client-side filter and sort (no refetch when toggling Mixed/Levels or All/Male/Female)
  const players = useMemo(() => {
    return allPlayers
      .filter(player => {
        if (genderFilter === 'all') return true;
        return player.players.gender?.toLowerCase() === genderFilter;
      })
      .map(player => ({
        ...player,
        current_elo: eloFilter === 'mixed' ? player.mixed_elo_rating : player.levels_elo_rating
      }))
      .sort((a, b) => b.current_elo - a.current_elo);
  }, [allPlayers, eloFilter, genderFilter]);

  // Reset to page 1 when filters or season change
  useEffect(() => {
    setCurrentPage(1);
  }, [eloFilter, genderFilter, leaderboardSeason?.id]);

  // Paginated slice for table display
  const totalPlayers = players.length;
  const totalPages = Math.max(1, Math.ceil(totalPlayers / itemsPerPage));
  const paginatedPlayers = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return players.slice(start, start + itemsPerPage);
  }, [players, currentPage, itemsPerPage]);

  const handlePageChange = (page, perPage) => {
    setCurrentPage(page);
    setItemsPerPage(perPage ?? itemsPerPage);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Typography variant="h4" className="mb-4">Loading...</Typography>
          <Typography variant="muted">Loading player data</Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Typography variant="h4" className="mb-4 text-danger-dark">Error</Typography>
          <Typography variant="muted">{error}</Typography>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      <Card variant="default" size="large">
        <h3 className={`${homeSectionHeading} mb-4`}>Player leaderboard</h3>

        <ClubAnalysisLeaderboardFilters
          seasons={seasons}
          leaderboardSeason={leaderboardSeason}
          onSeasonChange={setLeaderboardSeason}
          eloFilter={eloFilter}
          onEloFilterChange={setEloFilter}
          genderFilter={genderFilter}
          onGenderFilterChange={setGenderFilter}
        />

        <div className="mt-4">
          <AdminLeaderboardList
          players={paginatedPlayers}
          startRank={(currentPage - 1) * itemsPerPage + 1}
          eloLabel={eloFilter === 'mixed' ? 'Mixed ELO' : 'Levels ELO'}
          onViewAnalysis={onViewAnalysis}
          />
        </div>

        {totalPlayers > 0 && (
          <div className="mt-4 border-t border-border-medium pt-4">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={totalPlayers}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              showItemsPerPage={true}
              itemsPerPageOptions={[10, 30, 50, 100]}
            />
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card variant="default" size="large">
          <h3 className={`${homeSectionHeading} mb-4`}>Top performers</h3>
          <div className="space-y-3">
            {players.slice(0, 5).map((player, index) => (
              <div key={player.player_id} className="flex items-center justify-between p-3 bg-surface-subtle rounded-lg">
                <div className="flex items-center">
                  <span className="text-lg mr-3">
                    {index === 0 ? '🥇' : index === 1 ? '🥈' : index === 2 ? '🥉' : `#${index + 1}`}
                  </span>
                  <div>
                    <Typography variant="body" className={`font-medium ${getPlayerNameColor(player.players.gender)}`}>
                      {player.players.first_name} {player.players.last_name}
                    </Typography>
                    <Typography variant="subtle" className="text-sm">{player.session_count} sessions</Typography>
                  </div>
                </div>
                <div className="text-right">
                  <Typography variant="h3" className="font-bold text-blue-600">{player.current_elo}</Typography>
                  <Typography variant="subtle" className="text-xs">{eloFilter === 'mixed' ? 'Mixed' : 'Levels'}</Typography>
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Player Activity Summary */}
        <Card variant="default" size="large">
          <h3 className={`${homeSectionHeading} mb-4`}>Player activity summary</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-surface-subtle rounded-lg">
              <Typography variant="body" className="font-medium">Most Active Player</Typography>
              <Typography variant="body" className={`font-bold ${players.length > 0 ? getPlayerNameColor(players[0].players.gender) : 'text-text-primary'}`}>
                {players.length > 0 ? `${players[0].players.first_name} ${players[0].players.last_name}` : 'N/A'}
              </Typography>
            </div>
            <div className="flex justify-between items-center p-3 bg-surface-subtle rounded-lg">
              <Typography variant="body" className="font-medium">Average Sessions</Typography>
              <Typography variant="body" className="font-bold text-text-primary">
                {players.length > 0 ? Math.round(players.reduce((sum, p) => sum + p.session_count, 0) / players.length) : 0}
              </Typography>
            </div>
            <div className="flex justify-between items-center p-3 bg-surface-subtle rounded-lg">
              <Typography variant="body" className="font-medium">Highest ELO</Typography>
              <Typography variant="body" className="font-bold text-text-primary">
                {players.length > 0 ? players[0].current_elo : 'N/A'}
              </Typography>
            </div>
            <div className="flex justify-between items-center p-3 bg-surface-subtle rounded-lg">
              <Typography variant="body" className="font-medium">Total Players</Typography>
              <Typography variant="body" className="font-bold text-text-primary">{players.length}</Typography>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

// Player Analysis Dashboard Content Component
const PlayerAnalysisDashboardContent = ({ club, season, initialPlayer }) => {
  const [players, setPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(true);
  const [playersError, setPlayersError] = useState(null);

  const [selectedPlayerId, setSelectedPlayerId] = useState(initialPlayer?.playerId || '');
  const [selectedPlayerInfo, setSelectedPlayerInfo] = useState(initialPlayer || null);

  const [stats, setStats] = useState(null);
  const [eloHistory, setEloHistory] = useState([]);
  const [games, setGames] = useState([]);
  const [sessionSummaries, setSessionSummaries] = useState([]);

  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [analysisError, setAnalysisError] = useState(null);

  const [pairTypeFilter, setPairTypeFilter] = useState('all'); // 'all' | 'mixed' | 'levels'
  const [dateRangeFilter, setDateRangeFilter] = useState('season'); // 'last_10' | 'last_30_days' | 'season' | 'all_time'
  const [sessionFilter, setSessionFilter] = useState('all');
  const [gameTypeFilter, setGameTypeFilter] = useState('all');

  // Sync local selected player when coming from leaderboard
  useEffect(() => {
    if (initialPlayer?.playerId) {
      setSelectedPlayerId(initialPlayer.playerId);
      setSelectedPlayerInfo(initialPlayer);
    }
  }, [initialPlayer?.playerId]);

  // Load club players for selector
  useEffect(() => {
    if (!club?.id) return;

    const fetchPlayers = async () => {
      try {
        setPlayersLoading(true);
        setPlayersError(null);

        const { data, error } = await supabase
          .from('players_club')
          .select(`
            player_id,
            players!inner(
              id,
              first_name,
              last_name,
              email,
              gender,
              is_guest,
              is_active,
              merged_at,
              merged_into_user_id,
              deletion_requested_at
            )
          `)
          .eq('club_id', club.id)
          .eq('is_active', true);

        if (error) throw error;

        const mapped = sortClubPlayersByName(
          (data || [])
            .filter((row) => isSelectableClubRosterPlayer(row.players))
            .map(normalizeClubPlayerForSearch)
            .filter(Boolean)
            .map((player) => ({
              playerId: player.id,
              firstName: player.first_name,
              lastName: player.last_name,
              gender: player.gender,
              email: player.email,
              name: player.name,
            }))
        );

        setPlayers(mapped);

        // Don't auto-select - let user choose a player
      } catch (err) {
        console.error('Error fetching club players for analysis:', err);
        setPlayersError(err.message || 'Failed to load players');
      } finally {
        setPlayersLoading(false);
      }
    };

    fetchPlayers();
  }, [club?.id]);

  // Helper to filter games by current filters
  const filterGames = (allGames) => {
    if (!allGames || allGames.length === 0) return [];

    let filtered = [...allGames];

    // Pair type filter
    if (pairTypeFilter !== 'all') {
      filtered = filtered.filter((g) => (g.pair_type || 'levels') === pairTypeFilter);
    }

    // Session filter
    if (sessionFilter !== 'all') {
      filtered = filtered.filter((g) => g.session_games?.session_id === sessionFilter);
    }

    // Game type filter
    if (gameTypeFilter !== 'all') {
      filtered = filtered.filter(
        (g) =>
          g.game_type_name === gameTypeFilter ||
          g.game_type === gameTypeFilter
      );
    }

    // Date range filter
    const now = new Date();
    if (dateRangeFilter === 'last_30_days') {
      const cutoff = new Date();
      cutoff.setDate(now.getDate() - 30);
      filtered = filtered.filter((g) => new Date(g.created_at) >= cutoff);
    } else if (dateRangeFilter === 'last_10') {
      filtered = filtered
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
        .slice(0, 10);
    } else if (dateRangeFilter === 'season') {
      // Already scoped by seasonId when fetching; nothing extra needed
    }

    return filtered;
  };

  // Build per-session summary from filtered games
  const buildSessionSummaries = (filteredGames) => {
    const summaryMap = {};

    filteredGames.forEach((game) => {
      const sessionId = game.session_games?.session_id || 'unknown';
      const sessionName = game.sessionName || 'Unknown Session';

      if (!summaryMap[sessionId]) {
        summaryMap[sessionId] = {
          sessionId,
          sessionName,
          gamesPlayed: 0,
          wins: 0,
          losses: 0,
          totalEloChange: 0,
          opponentEloSamples: [],
        };
      }

      const entry = summaryMap[sessionId];
      entry.gamesPlayed += 1;
      if (game.result === 'win') {
        entry.wins += 1;
      } else if (game.result === 'loss') {
        entry.losses += 1;
      }
      if (typeof game.eloChange === 'number') {
        entry.totalEloChange += game.eloChange;
      }

      if (Array.isArray(game.opponents) && game.opponents.length > 0) {
        const validElos = game.opponents
          .map((o) => o.elo)
          .filter((elo) => typeof elo === 'number');
        if (validElos.length > 0) {
          const avgThisGame =
            validElos.reduce((sum, v) => sum + v, 0) / validElos.length;
          entry.opponentEloSamples.push(avgThisGame);
        }
      }
    });

    return Object.values(summaryMap).map((entry) => {
      const totalGames = entry.gamesPlayed || 0;
      const winRate =
        totalGames > 0 ? Math.round((entry.wins / totalGames) * 100) : 0;

      const avgOpponentElo =
        entry.opponentEloSamples.length > 0
          ? Math.round(
              entry.opponentEloSamples.reduce((s, v) => s + v, 0) /
                entry.opponentEloSamples.length
            )
          : null;

      // Simple suitability tag based on win rate and ELO delta
      let suitability = 'Balanced';
      let variant = 'info';
      if (winRate >= 65 && entry.totalEloChange > 0) {
        suitability = 'Comfortable';
        variant = 'success';
      } else if (winRate <= 40 && entry.totalEloChange <= 0) {
        suitability = 'Struggled';
        variant = 'danger';
      }

      return {
        ...entry,
        winRate,
        avgOpponentElo,
        suitability,
        variant,
      };
    });
  };

  // Load analysis data when player or filters change
  useEffect(() => {
    if (!selectedPlayerId || !club?.id) return;

    const fetchAnalysis = async () => {
      try {
        setAnalysisLoading(true);
        setAnalysisError(null);

        const seasonId = season?.id || null;

        // Fetch high-level stats (gender, ELOs, sessions/games)
        const clubStats = await fetchPlayerClubStats(
          selectedPlayerId,
          club.id,
          seasonId
        );
        setStats(clubStats);

        // Fetch game history
        const allGames = await fetchPlayerUnifiedGameHistory(
          selectedPlayerId,
          club.id,
          seasonId,
          200
        );
        setGames(allGames);

        const filtered = filterGames(allGames);
        setSessionSummaries(buildSessionSummaries(filtered));

        // Fetch elo history for chart
        const eloHist = await fetchPlayerEloHistory(
          selectedPlayerId,
          club.id,
          seasonId,
          pairTypeFilter === 'all' ? 'all' : pairTypeFilter
        );
        setEloHistory(eloHist);
      } catch (err) {
        console.error('Error fetching player analysis data:', err);
        setAnalysisError(err.message || 'Failed to load player analysis');
      } finally {
        setAnalysisLoading(false);
      }
    };

    fetchAnalysis();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayerId, club?.id, season?.id, pairTypeFilter, dateRangeFilter, sessionFilter, gameTypeFilter]);

  const filteredGames = filterGames(games);

  const sessionOptions = Array.from(
    new Map(
      games
        .filter((g) => g.session_games?.session_id)
        .map((g) => [
          g.session_games.session_id,
          {
            id: g.session_games.session_id,
            name: g.sessionName || 'Unknown Session',
            // Use the game created_at as an approximation of session date
            displayDate: g.created_at
              ? new Date(g.created_at).toLocaleDateString('en-GB', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })
              : null,
          },
        ])
    ).values()
  );

  const distinctGameTypes = Array.from(
    new Set(
      games
        .map((g) => g.game_type_name || g.game_type)
        .filter(Boolean)
    )
  );

  const overallWinRate =
    filteredGames.length > 0
      ? Math.round(
          (filteredGames.filter((g) => g.result === 'win').length /
            filteredGames.length) *
            100
        )
      : 0;

  const sessionsPlayedFromGames = React.useMemo(() => {
    if (!games || games.length === 0) return 0;
    const sessionIds = new Set(
      games
        .map((g) => g.session_games?.session_id)
        .filter((id) => !!id)
    );
    return sessionIds.size;
  }, [games]);

  const lastSessionFromGames = React.useMemo(() => {
    if (!games || games.length === 0) return null;

    const latestGame = games.reduce((latest, game) => {
      if (!game.created_at) return latest;
      if (!latest) return game;
      return new Date(game.created_at) > new Date(latest.created_at)
        ? game
        : latest;
    }, null);

    if (!latestGame || !latestGame.created_at) return null;

    const date = new Date(latestGame.created_at);
    const formattedDate = date.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });

    const today = new Date();
    const diffMs = today.setHours(0, 0, 0, 0) - date.setHours(0, 0, 0, 0);
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    let relativeLabel = 'Today';
    if (diffDays === 1) {
      relativeLabel = '1 day ago';
    } else if (diffDays > 1) {
      relativeLabel = `${diffDays} days ago`;
    }

    return {
      formattedDate,
      relativeLabel,
      name: latestGame.sessionName || 'Unknown Session',
    };
  }, [games]);

  const playerKpiItems = selectedPlayerId && selectedPlayerInfo ? [
    {
      key: 'gender',
      label: 'Gender',
      value: selectedPlayerInfo.gender === 'female' ? 'Female' : 'Male',
      icon: selectedPlayerInfo.gender === 'female' ? '♀️' : '♂️',
      iconBg: selectedPlayerInfo.gender === 'female' ? ANALYTICS_ICON_BG.purple : ANALYTICS_ICON_BG.blue,
    },
    {
      key: 'sessions',
      label: 'Sessions (season)',
      mobileLabel: 'Sessions',
      value: sessionsPlayedFromGames,
      icon: '🏸',
      iconBg: ANALYTICS_ICON_BG.green,
    },
    {
      key: 'mixed-elo',
      label: 'Mixed ELO',
      value: stats?.mixedElo ?? '—',
      icon: '📊',
      iconBg: ANALYTICS_ICON_BG.purple,
    },
    {
      key: 'levels-elo',
      label: 'Levels ELO',
      value: stats?.levelsElo ?? '—',
      icon: '📈',
      iconBg: ANALYTICS_ICON_BG.orange,
    },
    {
      key: 'membership',
      label: 'Membership',
      value: stats?.isGuest ? 'Guest' : 'Member',
      icon: '👤',
      iconBg: ANALYTICS_ICON_BG.blue,
    },
    {
      key: 'games',
      label: 'Total games',
      value: stats?.totalGamesPlayed ?? filteredGames.length,
      icon: '🎾',
      iconBg: ANALYTICS_ICON_BG.green,
    },
    {
      key: 'win-rate',
      label: 'Win rate',
      value: `${overallWinRate}%`,
      layout: 'inline',
      icon: '🏆',
      iconBg: ANALYTICS_ICON_BG.orange,
    },
    {
      key: 'last-session',
      label: 'Last session',
      value: lastSessionFromGames?.formattedDate ?? '—',
      subtitle: lastSessionFromGames
        ? `${lastSessionFromGames.relativeLabel} · ${lastSessionFromGames.name}`
        : 'No sessions yet',
      fullWidth: true,
      layout: 'stacked',
      icon: '📅',
      iconBg: ANALYTICS_ICON_BG.blue,
    },
  ] : [];

  return (
    <div className="space-y-6">
      {/* High-level summary */}
      <Card variant="default" size="large">
        <h3 className={`${homeSectionHeading} mb-4`}>Player analysis</h3>
        <div className="mb-6 flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
          <Typography variant="label" className="shrink-0 whitespace-nowrap">
            Select player
          </Typography>
          <div className="w-full max-w-md flex-1">
            {playersLoading ? (
              <Typography variant="muted">Loading players...</Typography>
            ) : playersError ? (
              <Typography variant="muted" className="text-danger-dark">
                {playersError}
              </Typography>
            ) : (
              <PlayerSearchInput
                placeholder="Search players..."
                availablePlayers={players.map((p) => ({
                  id: p.playerId,
                  first_name: p.firstName,
                  last_name: p.lastName,
                  gender: p.gender,
                  email: p.email,
                  name: p.name || getPlayerFullName(p),
                }))}
                selectedPlayer={
                  selectedPlayerInfo
                    ? {
                        id: selectedPlayerInfo.playerId,
                        first_name: selectedPlayerInfo.firstName,
                        last_name: selectedPlayerInfo.lastName,
                        gender: selectedPlayerInfo.gender,
                      }
                    : null
                }
                onSelectPlayer={(player) => {
                  setSelectedPlayerId(player.id);
                  setSelectedPlayerInfo({
                    playerId: player.id,
                    firstName: player.first_name,
                    lastName: player.last_name,
                    gender: player.gender,
                  });
                }}
                onRemovePlayer={() => {
                  setSelectedPlayerId('');
                  setSelectedPlayerInfo(null);
                }}
                showSelectedInfo={false}
                className="w-full"
              />
            )}
          </div>
        </div>

        {analysisLoading ? (
          <Typography variant="muted">Loading player analysis...</Typography>
        ) : analysisError ? (
          <Typography variant="muted" className="text-danger-dark">
            {analysisError}
          </Typography>
        ) : !selectedPlayerId || !selectedPlayerInfo ? (
          <div className="py-12 text-center">
            <div className="mb-4 text-6xl">🔍</div>
            <Heading level={3} className="mb-2">Select a player to view analysis</Heading>
            <Typography variant="muted" className="text-text-secondary">
              Use the search box above to find a player and view their performance statistics
            </Typography>
          </div>
        ) : (
          <ClubAnalysisKpiGrid items={playerKpiItems} dense />
        )}
      </Card>

      {selectedPlayerId && selectedPlayerInfo ? (
        <>
          {/* ELO chart + filters */}
          <Card variant="default" size="large">
            <h4 className={`${homeSectionHeading} mb-4 text-base sm:text-lg`}>ELO performance over time</h4>
            <ClubAnalysisPlayerFilters
              pairTypeFilter={pairTypeFilter}
              onPairTypeFilterChange={setPairTypeFilter}
              gameTypeFilter={gameTypeFilter}
              onGameTypeFilterChange={setGameTypeFilter}
              gameTypeOptions={distinctGameTypes}
              sessionFilter={sessionFilter}
              onSessionFilterChange={setSessionFilter}
              sessionOptions={sessionOptions}
              dateRangeFilter={dateRangeFilter}
              onDateRangeFilterChange={setDateRangeFilter}
            />
            <div className="mt-4 h-64 sm:h-80">
              {analysisLoading ? (
                <div className="flex h-full items-center justify-center">
                  <Typography variant="muted">Loading chart…</Typography>
                </div>
              ) : eloHistory && eloHistory.length > 0 ? (
                <EloHistoryChart
                  eloHistory={eloHistory}
                  pairType={pairTypeFilter === 'all' ? 'all' : pairTypeFilter}
                  selectedPlayer={null}
                />
              ) : (
                <div className="flex h-full items-center justify-center">
                  <Typography variant="muted">
                    No ELO history available for this player with the current filters.
                  </Typography>
                </div>
              )}
            </div>
          </Card>

          {/* Session suitability */}
          <Card variant="default" size="large">
            <h4 className={`${homeSectionHeading} mb-3 text-base sm:text-lg`}>Session suitability</h4>
            {analysisLoading ? (
              <Typography variant="muted">Loading session summary…</Typography>
            ) : sessionSummaries.length === 0 ? (
              <Typography variant="muted">
                No session summary available yet. Play more games or adjust filters.
              </Typography>
            ) : (
              <ClubAnalysisSessionSummaryList
                summaries={sessionSummaries}
                selectedSessionId={sessionFilter}
                onSelectSession={setSessionFilter}
              />
            )}
          </Card>

          {/* Game history */}
          {analysisLoading ? (
            <Card variant="default" size="large">
              <Typography variant="muted">Loading game history…</Typography>
            </Card>
          ) : filteredGames.length === 0 ? (
            <Card variant="default" size="large">
              <h4 className={`${homeSectionHeading} mb-3 text-base sm:text-lg`}>Game history</h4>
              <Typography variant="muted">
                No games found for this player with the current filters.
              </Typography>
            </Card>
          ) : (
            <GameHistoryTable
              games={filteredGames}
              pageSize={10}
              hideFilters
              clubId={club?.id}
              subjectPlayerLabel={getPlayerFullName({
                first_name: selectedPlayerInfo.firstName,
                last_name: selectedPlayerInfo.lastName,
                name: selectedPlayerInfo.name,
              })}
            />
          )}
        </>
      ) : null}
    </div>
  );
};

// Main Dashboard Content with Tabs
const DashboardContent = ({ analytics, season, club, userRole }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [analysisPlayer, setAnalysisPlayer] = useState(null);

  const handleViewAnalysis = useCallback((playerInfo) => {
    setAnalysisPlayer(playerInfo);
    setActiveTab('analysis');
  }, []);

  const tabs = useMemo(
    () => [
      {
        id: 'general',
        label: 'General',
        mobileLabel: 'General',
        icon: '📊',
        content: (
          <GeneralDashboardContent analytics={analytics} season={season} club={club} userRole={userRole} />
        ),
      },
      {
        id: 'players',
        label: 'Leaderboard',
        mobileLabel: 'Board',
        icon: '👥',
        content: (
          <ClubPlayersDashboardContent
            club={club}
            season={season}
            onViewAnalysis={handleViewAnalysis}
          />
        ),
      },
      {
        id: 'analysis',
        label: 'Player analysis',
        mobileLabel: 'Analysis',
        icon: '🧠',
        content: (
          <PlayerAnalysisDashboardContent
            club={club}
            season={season}
            initialPlayer={analysisPlayer}
          />
        ),
      },
    ],
    [analytics, season, club, userRole, analysisPlayer, handleViewAnalysis]
  );

  return (
    <Tabs
      tabs={tabs}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      className="mb-6"
    />
  );
};

const ClubAnalysisContent = ({ currentUser, club, userRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { season } = useSeason();
  
  // Analytics state (exact copy from original ClubAdmin)
  const [analytics, setAnalytics] = useState({
    totalPlayers: 0,
    activePlayers: 0,
    totalSessions: 0,
    sessionsThisMonth: 0,
    averageSessionSize: 0,
    genderSplit: { male: 0, female: 0, other: 0 },
    topPlayers: [],
    recentSessions: [],
    playerRetention: 0,
    activePlayers30Days: 0,
    activePlayers30DaysPercentage: 0,
    averageElo: { mixed: 0, levels: 0 }
  });
  
  // Loading state for analytics
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch analytics data (exact copy from original ClubAdmin)
  useEffect(() => {
    if (!club?.id) return;
    
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        setError(null);


        // Get current season for filtering
        const currentSeason = season?.id;

        // Fetch total players
        const { count: totalPlayers, error: playersError } = await supabase
          .from('players_club')
          .select('*', { count: 'exact', head: true })
          .eq('club_id', club.id)
          .eq('is_active', true);

        if (playersError) throw playersError;

        // Fetch active players (played in last 30 days)
        const thirtyDaysAgo = new Date();
        thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
        
        // First get sessions for this club in the last 30 days
        const { data: recentClubSessions, error: recentSessionsError } = await supabase
          .from('sessions')
          .select('id')
          .eq('club_id', club.id)
          .gte('session_date', thirtyDaysAgo.toISOString());

        if (recentSessionsError) throw recentSessionsError;

        // Then get signups for those sessions
        let activePlayers = 0;
        if (recentClubSessions && recentClubSessions.length > 0) {
          const sessionIds = recentClubSessions.map(s => s.id);
          const { count, error: activeError } = await supabase
            .from('session_signups')
            .select('*', { count: 'exact', head: true })
            .in('session_id', sessionIds)
            .eq('signup_status', 'confirmed');

          if (activeError) throw activeError;
          activePlayers = count || 0;
        }

        // Fetch total sessions
        const { count: totalSessions, error: sessionsError } = await supabase
          .from('sessions')
          .select('*', { count: 'exact', head: true })
          .eq('club_id', club.id);

        if (sessionsError) throw sessionsError;

        // Fetch sessions this month
        const startOfMonth = new Date();
        startOfMonth.setDate(1);
        startOfMonth.setHours(0, 0, 0, 0);

        const { count: sessionsThisMonth, error: monthSessionsError } = await supabase
          .from('sessions')
          .select('*', { count: 'exact', head: true })
          .eq('club_id', club.id)
          .gte('session_date', startOfMonth.toISOString().split('T')[0]);

        if (monthSessionsError) throw monthSessionsError;

        // Fetch recent sessions
        const { data: recentSessions, error: recentError } = await supabase
          .from('sessions')
          .select(`
            id,
            name,
            description,
            session_date,
            session_status
          `)
          .eq('club_id', club.id)
          .order('session_date', { ascending: false })
          .limit(10);

        if (recentError) throw recentError;

        // Get player and game counts for each session
        const processedRecentSessions = await Promise.all(
          recentSessions.map(async (session) => {
            // Get player count
            const { count: playerCount } = await supabase
              .from('session_signups')
              .select('*', { count: 'exact', head: true })
              .eq('session_id', session.id)
              .eq('signup_status', 'confirmed');

            // Get game count
            const { count: gameCount } = await supabase
              .from('session_games')
              .select('*', { count: 'exact', head: true })
              .eq('session_id', session.id);

            return {
              ...session,
              player_count: playerCount || 0,
              game_count: gameCount || 0
            };
          })
        );

        // Fetch gender split
        const { data: genderData, error: genderError } = await supabase
          .from('players_club')
          .select(`
            players(gender)
          `)
          .eq('club_id', club.id)
          .eq('is_active', true);

        if (genderError) throw genderError;

        const genderSplit = genderData.reduce((acc, item) => {
          const gender = item.players?.gender || 'other';
          acc[gender] = (acc[gender] || 0) + 1;
          return acc;
        }, { male: 0, female: 0, other: 0 });

        // Fetch average ELO ratings
        const { data: eloData, error: eloError } = await supabase
          .from('player_club_elo_ratings')
          .select('mixed_elo_rating, levels_elo_rating')
          .eq('club_id', club.id);

        if (eloError) throw eloError;

        const averageElo = eloData.reduce((acc, item) => {
          acc.mixed += item.mixed_elo_rating || 1200;
          acc.levels += item.levels_elo_rating || 1200;
          return acc;
        }, { mixed: 0, levels: 0 });

        if (eloData.length > 0) {
          averageElo.mixed = averageElo.mixed / eloData.length;
          averageElo.levels = averageElo.levels / eloData.length;
        }

        // Calculate average session size
        const totalPlayerSignups = processedRecentSessions.reduce((sum, session) => sum + session.player_count, 0);
        const averageSessionSize = processedRecentSessions.length > 0 
          ? Math.round(totalPlayerSignups / processedRecentSessions.length) 
          : 0;

        // Calculate active players in last 30 days (reuse thirtyDaysAgo from above)
        const { data: sessionsLast30Days, error: sessions30DaysError } = await supabase
          .from('sessions')
          .select('id')
          .eq('club_id', club.id)
          .gte('session_date', thirtyDaysAgo.toISOString().split('T')[0]);

        if (sessions30DaysError) throw sessions30DaysError;

        let activePlayers30Days = 0;
        let activePlayers30DaysPercentage = 0;

        if (sessionsLast30Days && sessionsLast30Days.length > 0) {
          const recentSessionIds = sessionsLast30Days.map(s => s.id);
          const { data: recentPlayers, error: recentPlayersError } = await supabase
            .from('session_players')
            .select('player_id')
            .in('session_id', recentSessionIds);

          if (recentPlayersError) throw recentPlayersError;

          activePlayers30Days = new Set(recentPlayers.map(p => p.player_id)).size;
          activePlayers30DaysPercentage = totalPlayers > 0 ? Math.round((activePlayers30Days / totalPlayers) * 100) : 0;
        }

        const playerRetention = activePlayers30DaysPercentage;

        const finalAnalytics = {
          totalPlayers: totalPlayers || 0,
          activePlayers: activePlayers || 0,
          totalSessions: totalSessions || 0,
          sessionsThisMonth: sessionsThisMonth || 0,
          averageSessionSize,
          genderSplit,
          topPlayers: [], // Could be implemented later
          recentSessions: processedRecentSessions,
          playerRetention,
          activePlayers30Days,
          activePlayers30DaysPercentage,
          averageElo
        };

        setAnalytics(finalAnalytics);

      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [club?.id, season?.id]);

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'owner':
        return 'info';
      case 'admin':
        return 'primary';
      case 'host':
        return 'success';
      default:
        return 'default';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'owner':
        return 'Owner';
      case 'admin':
        return 'Admin';
      case 'host':
        return 'Host';
      default:
        return role;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Typography variant="h4" className="mb-4">Loading...</Typography>
          <Typography variant="muted">Loading club analytics</Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Typography variant="h4" className="mb-4 text-danger-dark">Error</Typography>
          <Typography variant="muted" className="mb-4">{error}</Typography>
          <Button
            onClick={() => navigate('/my-clubs')}
            variant="primary"
          >
            Back to Manage Clubs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto max-w-app-shell px-3 py-6 sm:px-6 sm:py-8">
        {/* Back Button - Moved to top left */}
        <div className="mb-8">
          <BackButton
            onClick={() => {
              navigateBack(navigate, location, club.id, `/club-management/${club.id}`);
            }}
          >
            Back to Management
          </BackButton>
        </div>

        {/* Header */}
        <div className="text-center mb-16">
          <div className="flex items-center justify-center mb-6">
            <div className="text-6xl mr-6">📊</div>
            <div>
              <Heading level={1} className="text-display text-text-primary mb-2">
                {club.name} - Analysis
              </Heading>
              <StatusBadge variant={getRoleBadgeVariant(userRole)} size="medium">
                {getRoleDisplayName(userRole)}
              </StatusBadge>
            </div>
          </div>
          <Text className="text-body-large text-text-secondary max-w-2xl mx-auto">
            Analytics, insights, and performance metrics for your club
          </Text>
        </div>

        {/* Dashboard Content */}
        <DashboardContent
          analytics={analytics}
          season={season}
          club={club}
          userRole={userRole}
        />
      </div>
    </div>
  );
};

// Main wrapper component
const ClubAnalysis = ({ currentUser }) => {
  const locationState = useLocation();
  const navigate = useNavigate();
  const [club, setClub] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Get club and role from location state
    if (locationState.state?.selectedClub && locationState.state?.userRole) {
      setClub(locationState.state.selectedClub);
      setUserRole(locationState.state.userRole);
      setLoading(false);
    } else {
      setError('No club information provided');
      setLoading(false);
    }
  }, [locationState.state]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Typography variant="h4" className="mb-4 text-text-primary">Loading...</Typography>
          <Typography variant="muted">Loading club information</Typography>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <Typography variant="h4" className="mb-4 text-danger-dark">Error</Typography>
          <Typography variant="muted" className="mb-4">{error}</Typography>
          <Button
            onClick={() => navigate('/my-clubs')}
            variant="primary"
          >
            Back to Manage Clubs
          </Button>
        </div>
      </div>
    );
  }

  return (
    <SeasonProvider clubId={club?.id}>
      <ClubAnalysisContent 
        currentUser={currentUser}
        club={club}
        userRole={userRole}
      />
    </SeasonProvider>
  );
};

export default ClubAnalysis;